import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import { useToast } from '@/hooks/ToastProvider';

interface UseAppMutationOptions<TData, TVariables> extends Omit<
  UseMutationOptions<TData, ApiError, TVariables>,
  'mutationFn'
> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  successMessage?: string;
  showErrorToast?: boolean;
  showSuccessToast?: boolean;
}

/**
 * useAppMutation gives you a mutation with ready-to-use error/success toast UI.
 * Works for any API mutation that throws ApiError on failure.
 */
export function useAppMutation<TData, TVariables>({
  mutationFn,
  successMessage,
  showErrorToast = true,
  showSuccessToast = false,
  onSuccess,
  onError,
  ...options
}: UseAppMutationOptions<TData, TVariables>) {
  const { showToast } = useToast();

  return useMutation({
    mutationFn,
    onSuccess: (data, variables, context, mutation) => {
      if (showSuccessToast && successMessage) {
        showToast('success', successMessage, 3000);
      }
      onSuccess?.(data, variables, context, mutation);
    },
    onError: (error, variables, context, mutation) => {
      if (showErrorToast && error instanceof ApiError) {
        showToast(
          error.mappedError.severity as any,
          error.mappedError.message,
          4000
        );
      }
      onError?.(error, variables, context, mutation);
    },
    ...options
  });
}
